import classnames from 'classnames';
import React, { forwardRef, Ref, ChangeEvent, useId } from 'react';

import '@eika/checkbox-style';

import CheckboxError from './CheckboxError';

export interface CheckboxProps
    extends Omit<React.PropsWithoutRef<JSX.IntrinsicElements['input']>, 'onChange' | 'checked' | 'value'> {
    /**
     * Value for whether the checkbox is checked
     */
    checked?: boolean;
    /**
     * Label for the checkbox
     */
    label?: string;
    /**
     * Validation error
     */
    screenReaderOnlyLabel?: boolean;
    /**
     * onChange method for standard ChangeEvents for external libs
     */
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    /**
     * onChange method that returns checked and value (e.g. for usage with useState)
     */
    onValueChange?: (checked: boolean, value: string) => void;
    /**
     * The value for the checkbox
     */
    value?: string;
    /**
     * Custom id for the input tag
     */
    id?: string;
    /**
     * Adds classnames to input field, default classes are: 'sgw-checkbox' and 'sgw-checkbox__with_focus'.
     */
    className?: string;
    /**
     * Validation error
     */
    error?: React.ReactNode | string;
}

const Checkbox = forwardRef(
    (
        {
            className,
            value,
            onChange,
            onValueChange,
            label,
            id,
            checked,
            error,
            screenReaderOnlyLabel,
            ...rest
        }: CheckboxProps,
        ref: Ref<HTMLInputElement>,
    ): JSX.Element => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (onChange) onChange(e);
            if (onValueChange) onValueChange(e.target.checked, e.target.value);
        };
        return (
            <React.Fragment key={id}>
                <div className={classnames('sgw-checkbox sgw-checkbox__with_focus', className)}>
                    <input
                        type="checkbox"
                        value={value}
                        checked={checked}
                        onChange={onInputChange}
                        id={inputId}
                        ref={ref}
                        {...rest}
                    />
                    <label className={screenReaderOnlyLabel ? 'screen-reader-text' : ''} htmlFor={inputId}>
                        {label}
                    </label>
                </div>

                {error && <CheckboxError error={error} />}
            </React.Fragment>
        );
    },
);

export default Checkbox;
